/**
 * Timber List Update Modal
 * ------------------------
 * Preview the diff between an existing proposal's structures and a freshly
 * parsed timber CSV, then let the user opt-in/out of changes per structure
 * before applying.
 *
 * Sections:
 *  1. Updates to apply       (checkbox per structure, default checked)
 *  2. New structures to add  (checkbox per group, default checked)
 *  3. In proposal but not in CSV (checkbox per structure, default UNchecked
 *     — opt-in delete; the rest stay untouched)
 *  4. Unchanged              (collapsed, count only)
 *
 * The component is presentational + selection-state only; all writes happen
 * back in the parent's `onApply` handler with the returned selections.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, ChevronRight, ChevronDown, AlertTriangle, Plus, Minus, ArrowRight, ListOrdered, FileText, ChevronsUpDown } from 'lucide-react';
import type { ProposalTimberDiff, StructureDiff, LineChange, FieldDiff } from '../../utils/proposalTimberDiff';
import type { TimberDiffSelections } from '../../utils/proposalTimberApply';
import type { ProjectFile } from '../../types';
import './TimberListUpdateModal.css';

export type TimberSourceOption = Pick<
  ProjectFile,
  'id' | 'name' | 'uploadedAt' | 'uploadedByName' | 'versionLabel'
>;

export interface TimberListUpdateModalProps {
  diff: ProposalTimberDiff;
  /** File metadata for the source CSV (filename + uploaded-at), shown in the header. */
  source?: { fileName?: string; uploadedAt?: Date | null; uploadedByName?: string };
  /** Project Hub timber CSV versions available for switching (shown when length > 1). */
  sourceOptions?: TimberSourceOption[];
  selectedSourceId?: string;
  appliedSourceId?: string | null;
  onSelectSource?: (fileId: string) => void;
  sourceSwitchLoading?: boolean;
  /** Non-blocking validation hints to surface above the body (e.g. order-length warnings). */
  validationNotices?: string[];
  /** True when the proposal is sent/locked; the modal becomes informational only. */
  readOnly?: boolean;
  onCancel: () => void;
  onApply: (selections: TimberDiffSelections, opts: { openSupplierRfq: boolean }) => void;
}

const fmt2 = (n: number): string => {
  const v = Math.round(n * 100) / 100;
  return Number.isFinite(v) ? v.toString() : '—';
};

const fmtSignedBdft = (n: number): string => {
  const v = Math.round(n * 100) / 100;
  if (v === 0) return '±0';
  const sign = v > 0 ? '+' : '−';
  return `${sign}${Math.abs(v)}`;
};

const fmtDateShort = (d?: Date | null): string => {
  if (!d) return '';
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    }).format(d);
  } catch {
    return d.toString();
  }
};

interface SourceHeaderProps {
  source?: { fileName?: string; uploadedAt?: Date | null; uploadedByName?: string };
  sourceOptions?: TimberSourceOption[];
  selectedSourceId?: string;
  appliedSourceId?: string | null;
  onSelectSource?: (fileId: string) => void;
  sourceSwitchLoading?: boolean;
  readOnly?: boolean;
  onPopoverOpenChange?: (open: boolean) => void;
}

const SourceHeader: React.FC<SourceHeaderProps> = ({
  source,
  sourceOptions,
  selectedSourceId,
  appliedSourceId,
  onSelectSource,
  sourceSwitchLoading,
  readOnly,
  onPopoverOpenChange,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const setPopoverOpen = (next: boolean) => {
    setOpen(next);
    onPopoverOpenChange?.(next);
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const showPicker = !readOnly && sourceOptions && sourceOptions.length > 1 && onSelectSource;

  if (!showPicker) {
    return (
      <p className="tlu-source">
        {source?.fileName ? <strong>{source.fileName}</strong> : 'Imported CSV'}
        {source?.uploadedByName ? <> &middot; uploaded by {source.uploadedByName}</> : null}
        {source?.uploadedAt ? <> &middot; {fmtDateShort(source.uploadedAt)}</> : null}
      </p>
    );
  }

  const selectedOption = selectedSourceId
    ? sourceOptions.find(f => f.id === selectedSourceId)
    : undefined;
  const displayName = selectedOption?.name || source?.fileName || 'Imported CSV';
  const displayUploader = selectedOption?.uploadedByName || source?.uploadedByName;
  const displayDate = selectedOption?.uploadedAt || source?.uploadedAt;

  return (
    <div className="tlu-source-picker" ref={wrapRef}>
      <button
        type="button"
        className="tlu-source-trigger"
        onClick={() => setPopoverOpen(!open)}
        disabled={sourceSwitchLoading}
        aria-expanded={open}
        aria-haspopup="listbox"
        title="Change CSV source"
      >
        <span className="tlu-source-trigger-text">
          <strong>{displayName}</strong>
          {displayUploader && <> &middot; uploaded by {displayUploader}</>}
          {displayDate && <> &middot; {fmtDateShort(displayDate)}</>}
        </span>
        <span className="tlu-source-trigger-action">
          {sourceSwitchLoading ? (
            <>
              <span className="tlu-source-spinner" aria-hidden />
              Loading…
            </>
          ) : (
            <>
              Change source
              <ChevronsUpDown size={14} aria-hidden />
            </>
          )}
        </span>
      </button>

      {open && !sourceSwitchLoading && (
        <div className="tlu-source-popover" role="listbox" aria-label="Timber CSV versions">
          {sourceOptions.map((file, idx) => {
            const isSelected = file.id === selectedSourceId;
            const isLatest = idx === 0;
            const isApplied = !!appliedSourceId && appliedSourceId === file.id;
            return (
              <button
                key={file.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`tlu-source-option${isSelected ? ' tlu-source-option--selected' : ''}`}
                onClick={() => {
                  if (file.id && file.id !== selectedSourceId) {
                    onSelectSource(file.id);
                  }
                  setPopoverOpen(false);
                }}
              >
                <span className="tlu-source-option-icon" aria-hidden>
                  <FileText size={16} />
                </span>
                <span className="tlu-source-option-body">
                  <span className="tlu-source-option-title">
                    <span className="tlu-source-option-name">{file.name}</span>
                    {isLatest && <span className="tlu-source-badge tlu-source-badge--latest">Latest</span>}
                    {isApplied && <span className="tlu-source-badge tlu-source-badge--applied">Applied</span>}
                    {!isLatest && file.versionLabel && (
                      <span className="tlu-source-badge tlu-source-badge--version">{file.versionLabel}</span>
                    )}
                  </span>
                  <span className="tlu-source-option-meta">
                    {fmtDateShort(file.uploadedAt)}
                    {file.uploadedByName && (
                      <>
                        <span className="tlu-source-option-meta-dot">·</span>
                        by {file.uploadedByName}
                      </>
                    )}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TimberListUpdateModal: React.FC<TimberListUpdateModalProps> = ({
  diff,
  source,
  sourceOptions,
  selectedSourceId,
  appliedSourceId,
  onSelectSource,
  sourceSwitchLoading,
  validationNotices,
  readOnly,
  onCancel,
  onApply,
}) => {
  // Bucket the structure diffs once. Memoized so subsequent re-renders during
  // selection toggling don't repeat the same partition.
  const buckets = useMemo(() => {
    const updated: StructureDiff[] = [];
    const added: StructureDiff[] = [];
    const removedFromCsv: StructureDiff[] = [];
    const unchanged: StructureDiff[] = [];
    diff.structureDiffs.forEach(sd => {
      switch (sd.kind) {
        case 'updated': updated.push(sd); break;
        case 'added': added.push(sd); break;
        case 'removedFromCsv': removedFromCsv.push(sd); break;
        case 'unchanged': unchanged.push(sd); break;
      }
    });
    return { updated, added, removedFromCsv, unchanged };
  }, [diff]);

  // Selection state. Defaults follow the user-confirmed product spec:
  //   updates → all checked, new → all checked, removed → all UN-checked.
  const [applyIds, setApplyIds] = useState<Set<string>>(() =>
    new Set(buckets.updated.map(s => s.existingNodeSetId!).filter(Boolean)),
  );
  const [addNames, setAddNames] = useState<Set<string>>(() =>
    new Set(buckets.added.map(s => s.csvGroupName!).filter(Boolean)),
  );
  const [deleteIds, setDeleteIds] = useState<Set<string>>(new Set());
  const [openSupplierRfq, setOpenSupplierRfq] = useState(false);
  const [showUnchanged, setShowUnchanged] = useState(false);

  // Per-card collapse state. Defaults: updates open, new collapsed, removed collapsed.
  const [collapsedCards, setCollapsedCards] = useState<Set<string>>(() => {
    const s = new Set<string>();
    buckets.added.forEach(b => s.add(`add:${b.csvGroupName}`));
    buckets.removedFromCsv.forEach(b => s.add(`del:${b.existingNodeSetId}`));
    return s;
  });
  const [sourcePopoverOpen, setSourcePopoverOpen] = useState(false);

  // ESC closes source popover first, then modal; Cmd/Ctrl+Enter applies.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        if (sourcePopoverOpen) {
          e.preventDefault();
          setSourcePopoverOpen(false);
          return;
        }
        e.preventDefault();
        onCancel();
      } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (canApply) doApply();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // canApply / doApply intentionally omitted; we re-bind via stale closure each turn,
    // accepting that the very latest values are observed via the closures below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourcePopoverOpen]);

  const totalSelected = applyIds.size + addNames.size + deleteIds.size;
  const canApply = !readOnly && totalSelected > 0;
  // The "Match CSV order" affordance only makes sense when there's at least
  // one structure with a CSV counterpart whose lines could be reordered.
  const canReorder = !readOnly && (buckets.updated.length + buckets.unchanged.length) > 0;

  const toggleApply = (id: string) => {
    setApplyIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAdd = (name: string) => {
    setAddNames(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };
  const toggleDelete = (id: string) => {
    setDeleteIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleCard = (key: string) => {
    setCollapsedCards(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const doApply = () => {
    if (!canApply) return;
    onApply(
      {
        applyStructureIds: applyIds,
        addNewGroupNames: addNames,
        deleteStructureIds: deleteIds,
      },
      { openSupplierRfq },
    );
  };

  /**
   * "Match CSV order" — applies any selections currently ticked AND tells the
   * apply step to reorder every matched structure to the CSV's row order.
   * Always callable when at least one structure has a CSV counterpart, even
   * if no other selections are made (in which case it's a pure reorder).
   */
  const doMatchCsvOrder = () => {
    if (!canReorder) return;
    onApply(
      {
        applyStructureIds: applyIds,
        addNewGroupNames: addNames,
        deleteStructureIds: deleteIds,
        reorderToCsvOrder: true,
      },
      { openSupplierRfq },
    );
  };

  // Helpful UX cue when both new and removed are non-empty: "did you rename?"
  const showRenameHint = buckets.added.length > 0 && buckets.removedFromCsv.length > 0;

  return (
    <div className="tlu-overlay" role="dialog" aria-modal="true" aria-label="Update timber list from CSV">
      <div className="tlu-modal">
        <header className="tlu-header">
          <div className="tlu-header-text">
            <h2>Update Timber List from CSV</h2>
            <SourceHeader
              source={source}
              sourceOptions={sourceOptions}
              selectedSourceId={selectedSourceId}
              appliedSourceId={appliedSourceId}
              onSelectSource={onSelectSource}
              sourceSwitchLoading={sourceSwitchLoading}
              readOnly={readOnly}
              onPopoverOpenChange={setSourcePopoverOpen}
            />
          </div>
          <button type="button" className="tlu-close" onClick={onCancel} aria-label="Close" title="Close (Esc)">
            <X size={18} />
          </button>
        </header>

        {/* Summary chip strip */}
        <div className="tlu-summary" role="status" aria-live="polite">
          <SummaryChip label="updated" count={buckets.updated.length} kind="updated" />
          <SummaryChip label="new" count={buckets.added.length} kind="added" />
          <SummaryChip label="only in proposal" count={buckets.removedFromCsv.length} kind="removed" />
          <SummaryChip label="unchanged" count={buckets.unchanged.length} kind="unchanged" />
          <span className={`tlu-summary-bdft ${diff.summary.totalBdftDelta >= 0 ? 'tlu-summary-bdft--up' : 'tlu-summary-bdft--down'}`}>
            <span className="tlu-summary-bdft-label">Δ BDFT</span>
            <span className="tlu-summary-bdft-value">{fmtSignedBdft(diff.summary.totalBdftDelta)}</span>
          </span>
        </div>

        {validationNotices && validationNotices.length > 0 && (
          <div className="tlu-notice tlu-notice--warn" role="note">
            <AlertTriangle size={14} />
            <div>
              <strong>Heads up:</strong>
              <ul>
                {validationNotices.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </div>
          </div>
        )}

        {showRenameHint && (
          <div className="tlu-notice tlu-notice--info">
            Both new and proposal-only structures are present.
            If you renamed a structure, edit its name on the proposal first to match the CSV group, then re-open this preview.
          </div>
        )}

        {/* Body — sections */}
        <div className="tlu-body">
          {buckets.updated.length === 0 && buckets.added.length === 0 && buckets.removedFromCsv.length === 0 && (
            <div className="tlu-empty">
              No changes detected. The timber list is already up to date.
            </div>
          )}

          {buckets.updated.length > 0 && (
            <Section title="Updates to apply" subtitle="Existing structures whose lines changed in the CSV">
              {buckets.updated.map(sd => (
                <UpdatedCard
                  key={sd.existingNodeSetId}
                  sd={sd}
                  checked={applyIds.has(sd.existingNodeSetId!)}
                  collapsed={collapsedCards.has(`upd:${sd.existingNodeSetId}`)}
                  showUnchanged={showUnchanged}
                  onToggleChecked={() => toggleApply(sd.existingNodeSetId!)}
                  onToggleCollapsed={() => toggleCard(`upd:${sd.existingNodeSetId}`)}
                  disabled={readOnly}
                />
              ))}
              {buckets.updated.some(sd => sd.lineChanges.some(lc => lc.kind === 'unchanged')) && (
                <button
                  type="button"
                  className="tlu-toggle-unchanged"
                  onClick={() => setShowUnchanged(s => !s)}
                >
                  {showUnchanged ? 'Hide unchanged lines' : 'Show unchanged lines'}
                </button>
              )}
            </Section>
          )}

          {buckets.added.length > 0 && (
            <Section title="New structures to add" subtitle="Groups in the CSV that don't match any existing structure">
              {buckets.added.map(sd => (
                <AddedCard
                  key={`add:${sd.csvGroupName}`}
                  sd={sd}
                  checked={addNames.has(sd.csvGroupName!)}
                  collapsed={collapsedCards.has(`add:${sd.csvGroupName}`)}
                  onToggleChecked={() => toggleAdd(sd.csvGroupName!)}
                  onToggleCollapsed={() => toggleCard(`add:${sd.csvGroupName}`)}
                  disabled={readOnly}
                />
              ))}
            </Section>
          )}

          {buckets.removedFromCsv.length > 0 && (
            <Section
              title="In proposal but not in CSV"
              subtitle="Tick to delete these structures entirely. Default is to leave them untouched."
              tone="danger"
            >
              {buckets.removedFromCsv.map(sd => (
                <RemovedCard
                  key={`del:${sd.existingNodeSetId}`}
                  sd={sd}
                  checked={deleteIds.has(sd.existingNodeSetId!)}
                  collapsed={collapsedCards.has(`del:${sd.existingNodeSetId}`)}
                  onToggleChecked={() => toggleDelete(sd.existingNodeSetId!)}
                  onToggleCollapsed={() => toggleCard(`del:${sd.existingNodeSetId}`)}
                  disabled={readOnly}
                />
              ))}
            </Section>
          )}

          {buckets.unchanged.length > 0 && (
            <details className="tlu-unchanged-fold">
              <summary>{buckets.unchanged.length} structure{buckets.unchanged.length === 1 ? '' : 's'} unchanged</summary>
              <ul className="tlu-unchanged-list">
                {buckets.unchanged.map(sd => (
                  <li key={sd.existingNodeSetId}>{sd.existingName}</li>
                ))}
              </ul>
            </details>
          )}
        </div>

        {/* Footer */}
        <footer className="tlu-footer">
          <label className={`tlu-footer-checkbox ${readOnly ? 'tlu-footer-checkbox--disabled' : ''}`}>
            <input
              type="checkbox"
              checked={openSupplierRfq}
              onChange={e => setOpenSupplierRfq(e.target.checked)}
              disabled={readOnly}
            />
            Open supplier RFQ after applying
          </label>
          <div className="tlu-footer-spacer" />
          <button
            type="button"
            className="tlu-btn tlu-btn--ghost tlu-btn--icon"
            onClick={doMatchCsvOrder}
            disabled={!canReorder}
            title={
              readOnly
                ? 'Proposal is locked — unlock it first'
                : !canReorder
                  ? 'No matching structures to reorder'
                  : totalSelected > 0
                    ? `Apply selected changes and reorder every matched structure's timber list to follow the CSV row order`
                    : `Reorder every matched structure's timber list to follow the CSV row order — preserves all content`
            }
          >
            <ListOrdered size={14} />
            Match CSV order
          </button>
          <button type="button" className="tlu-btn tlu-btn--ghost" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="tlu-btn tlu-btn--primary"
            onClick={doApply}
            disabled={!canApply}
            title={readOnly ? 'Proposal is locked — unlock it first' : undefined}
          >
            {readOnly
              ? 'Proposal locked'
              : totalSelected === 0
                ? 'No changes selected'
                : `Apply ${describeSelection(applyIds.size, addNames.size, deleteIds.size)}`}
          </button>
        </footer>
      </div>
    </div>
  );
};

// ─── Subcomponents ─────────────────────────────────────────────────────────────

const Section: React.FC<{
  title: string;
  subtitle?: string;
  tone?: 'default' | 'danger';
  children: React.ReactNode;
}> = ({ title, subtitle, tone = 'default', children }) => (
  <section className={`tlu-section ${tone === 'danger' ? 'tlu-section--danger' : ''}`}>
    <div className="tlu-section-head">
      <h3>{title}</h3>
      {subtitle && <span className="tlu-section-subtitle">{subtitle}</span>}
    </div>
    <div className="tlu-section-body">{children}</div>
  </section>
);

const SummaryChip: React.FC<{
  label: string;
  count: number;
  kind: 'updated' | 'added' | 'removed' | 'unchanged';
}> = ({ label, count, kind }) => (
  <span className={`tlu-chip tlu-chip--${kind}`}>
    <span className="tlu-chip-count">{count}</span>
    <span className="tlu-chip-label">{label}</span>
  </span>
);

const UpdatedCard: React.FC<{
  sd: StructureDiff;
  checked: boolean;
  collapsed: boolean;
  showUnchanged: boolean;
  onToggleChecked: () => void;
  onToggleCollapsed: () => void;
  disabled?: boolean;
}> = ({ sd, checked, collapsed, showUnchanged, onToggleChecked, onToggleCollapsed, disabled }) => {
  const counts = sd.lineChanges.reduce(
    (acc, lc) => {
      if (lc.kind === 'modified') acc.mod += 1;
      else if (lc.kind === 'added') acc.add += 1;
      else if (lc.kind === 'removed') acc.rem += 1;
      else acc.same += 1;
      return acc;
    },
    { mod: 0, add: 0, rem: 0, same: 0 },
  );
  const delta = sd.totals.bdftAfter - sd.totals.bdftBefore;
  const visibleLines = showUnchanged
    ? sd.lineChanges
    : sd.lineChanges.filter(lc => lc.kind !== 'unchanged');

  return (
    <article className={`tlu-card tlu-card--updated ${checked ? '' : 'tlu-card--off'}`}>
      <header className="tlu-card-head">
        <label className="tlu-card-check">
          <input
            type="checkbox"
            checked={checked}
            onChange={onToggleChecked}
            disabled={disabled}
            aria-label={`Apply updates to ${sd.existingName}`}
          />
        </label>
        <button
          type="button"
          className="tlu-card-disclosure"
          onClick={onToggleCollapsed}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        </button>
        <div className="tlu-card-title">
          <span className="tlu-card-name">{sd.existingName}</span>
          <span className="tlu-card-meta">
            {pillize(counts.mod, 'changed')}
            {pillize(counts.add, 'added')}
            {pillize(counts.rem, 'removed')}
          </span>
        </div>
        <span className={`tlu-card-bdft ${delta >= 0 ? 'tlu-card-bdft--up' : 'tlu-card-bdft--down'}`}>
          Δ {fmtSignedBdft(delta)} BDFT
        </span>
      </header>
      {!collapsed && (
        <LineTable lines={visibleLines} mode="modified" />
      )}
    </article>
  );
};

const AddedCard: React.FC<{
  sd: StructureDiff;
  checked: boolean;
  collapsed: boolean;
  onToggleChecked: () => void;
  onToggleCollapsed: () => void;
  disabled?: boolean;
}> = ({ sd, checked, collapsed, onToggleChecked, onToggleCollapsed, disabled }) => (
  <article className={`tlu-card tlu-card--added ${checked ? '' : 'tlu-card--off'}`}>
    <header className="tlu-card-head">
      <label className="tlu-card-check">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggleChecked}
          disabled={disabled}
          aria-label={`Add new structure ${sd.csvGroupName}`}
        />
      </label>
      <button
        type="button"
        className="tlu-card-disclosure"
        onClick={onToggleCollapsed}
        aria-expanded={!collapsed}
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
      </button>
      <div className="tlu-card-title">
        <span className="tlu-card-name">{sd.csvGroupName}</span>
        <span className="tlu-card-meta">
          <span className="tlu-pill tlu-pill--added"><Plus size={10} /> {sd.totals.lineAfter} lines</span>
        </span>
      </div>
      <span className="tlu-card-bdft tlu-card-bdft--up">+{fmt2(sd.totals.bdftAfter)} BDFT</span>
    </header>
    {!collapsed && <LineTable lines={sd.lineChanges} mode="added" />}
  </article>
);

const RemovedCard: React.FC<{
  sd: StructureDiff;
  checked: boolean;
  collapsed: boolean;
  onToggleChecked: () => void;
  onToggleCollapsed: () => void;
  disabled?: boolean;
}> = ({ sd, checked, collapsed, onToggleChecked, onToggleCollapsed, disabled }) => (
  <article className={`tlu-card tlu-card--removed ${checked ? 'tlu-card--will-delete' : ''}`}>
    <header className="tlu-card-head">
      <label className="tlu-card-check">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggleChecked}
          disabled={disabled}
          aria-label={`Delete ${sd.existingName}`}
        />
      </label>
      <button
        type="button"
        className="tlu-card-disclosure"
        onClick={onToggleCollapsed}
        aria-expanded={!collapsed}
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
      </button>
      <div className="tlu-card-title">
        <span className="tlu-card-name">{sd.existingName}</span>
        <span className="tlu-card-meta">
          <span className="tlu-pill tlu-pill--removed"><Minus size={10} /> {sd.totals.lineBefore} lines</span>
          {checked && <span className="tlu-pill tlu-pill--danger">Will delete</span>}
        </span>
      </div>
      <span className="tlu-card-bdft tlu-card-bdft--down">−{fmt2(sd.totals.bdftBefore)} BDFT</span>
    </header>
    {!collapsed && <LineTable lines={sd.lineChanges} mode="removed" />}
  </article>
);

const LineTable: React.FC<{
  lines: LineChange[];
  mode: 'modified' | 'added' | 'removed';
}> = ({ lines, mode }) => {
  if (lines.length === 0) {
    return <div className="tlu-line-empty">No line-level changes to show.</div>;
  }
  return (
    <table className="tlu-line-table">
      <thead>
        <tr>
          <th className="tlu-col-kind"></th>
          <th>Piece</th>
          <th>Dimensions</th>
          <th className="tlu-num">Qty</th>
          <th className="tlu-num">BDFT</th>
        </tr>
      </thead>
      <tbody>
        {lines.map(lc => (
          <LineRow key={`${lc.kind}:${lc.matchKey}`} lc={lc} mode={mode} />
        ))}
      </tbody>
    </table>
  );
};

const LineRow: React.FC<{ lc: LineChange; mode: 'modified' | 'added' | 'removed' }> = ({ lc, mode }) => {
  const kindClass = `tlu-row tlu-row--${lc.kind}`;

  if (lc.kind === 'unchanged' && lc.before) {
    return (
      <tr className={kindClass}>
        <td className="tlu-col-kind"><span className="tlu-row-tag tlu-row-tag--neutral" /></td>
        <td>{lc.before.name}</td>
        <td>{lc.before.dimensions}</td>
        <td className="tlu-num">{lc.before.quantity}</td>
        <td className="tlu-num">{fmt2(lc.before.boardFeet)}</td>
      </tr>
    );
  }

  if (lc.kind === 'added' && lc.after) {
    return (
      <tr className={kindClass}>
        <td className="tlu-col-kind"><span className="tlu-row-tag tlu-row-tag--add" aria-label="Added"><Plus size={10} /></span></td>
        <td>{lc.after.pieceName}</td>
        <td>{lc.after.dimensions}</td>
        <td className="tlu-num">{lc.after.quantity}</td>
        <td className="tlu-num">{fmt2(lc.after.boardFeet)}</td>
      </tr>
    );
  }

  if (lc.kind === 'removed' && lc.before) {
    return (
      <tr className={kindClass}>
        <td className="tlu-col-kind"><span className="tlu-row-tag tlu-row-tag--rem" aria-label="Removed"><Minus size={10} /></span></td>
        <td className="tlu-strike">{lc.before.name}</td>
        <td className="tlu-strike">{lc.before.dimensions}</td>
        <td className="tlu-num tlu-strike">{lc.before.quantity}</td>
        <td className="tlu-num tlu-strike">{fmt2(lc.before.boardFeet)}</td>
      </tr>
    );
  }

  if (lc.kind === 'modified' && lc.before && lc.after) {
    const fields = new Map<string, FieldDiff>();
    lc.fieldDiffs.forEach(fd => fields.set(fd.field, fd));
    const renderCell = (
      key: 'pieceName' | 'dimensions' | 'quantity' | 'boardFeet',
      beforeVal: string | number,
      afterVal: string | number,
      numeric?: boolean,
    ) => {
      const fd = fields.get(key);
      if (!fd) {
        return <span className={numeric ? 'tlu-num' : ''}>{beforeVal}</span>;
      }
      return (
        <span className={`tlu-cell-change ${numeric ? 'tlu-num' : ''}`}>
          <span className="tlu-cell-before">{fmtCell(fd.before)}</span>
          <ArrowRight size={10} className="tlu-cell-arrow" aria-hidden />
          <span className="tlu-cell-after">{fmtCell(fd.after)}</span>
        </span>
      );
    };
    return (
      <tr className={kindClass}>
        <td className="tlu-col-kind"><span className="tlu-row-tag tlu-row-tag--mod" aria-label="Modified" /></td>
        <td>{renderCell('pieceName', lc.before.name, lc.after.pieceName)}</td>
        <td>{renderCell('dimensions', lc.before.dimensions, lc.after.dimensions)}</td>
        <td className="tlu-num">{renderCell('quantity', lc.before.quantity, lc.after.quantity, true)}</td>
        <td className="tlu-num">{renderCell('boardFeet', fmt2(lc.before.boardFeet), fmt2(lc.after.boardFeet), true)}</td>
      </tr>
    );
  }

  return null;
};

// ─── Tiny helpers ──────────────────────────────────────────────────────────────

function pillize(count: number, label: string): React.ReactNode {
  if (!count) return null;
  return (
    <span className={`tlu-pill tlu-pill--${label}`}>{count} {label}</span>
  );
}

function fmtCell(v: string | number): string {
  if (typeof v === 'number') return fmt2(v);
  return v;
}

function describeSelection(updates: number, news: number, deletes: number): string {
  const parts: string[] = [];
  if (updates) parts.push(`${updates} update${updates === 1 ? '' : 's'}`);
  if (news) parts.push(`${news} new`);
  if (deletes) parts.push(`${deletes} delete${deletes === 1 ? '' : 's'}`);
  return parts.length ? parts.join(', ') : 'changes';
}

export default TimberListUpdateModal;
